<template>
    <v-main>
      <v-container class="fill-height" fluid>
        <v-row align="center" justify="center">
          <v-col cols="12" sm="8" md="4">
            <v-card elevation="2" class="pa-6">
              <v-card-title class="justify-center">
                <v-img src="/img/sysclass_logo.png" contain max-width="150" class="mb-2" />
              </v-card-title>
              <v-card-text>
                <p class="text-center mb-6">Faça login e acesse sua conta do Sysclass</p>
				<FormsTextField 
					v-model="email" 
					label="E-mail" 
					:error-messages="errors.email"
					outlined 
					dense 
					required 
					type="email" 
					:autofocus="!requiredPassword"
					:disabled="loading"
					@keyup-enter="sendEmail()"
				/>

				<v-checkbox
					v-model="rememberEmail"
					label="Lembrar meu e-mail"
					dense
					color="primary"
					class="mt-2"
					:disabled="loading"
				/>

				<forms-text-field 
					v-if="requiredPassword"
					v-model="password" 
					label="Senha" 
					outlined 
					dense 
					required 
					:type="showPassword ? 'text' : 'password'"
					:disabled="loading"
					class="mt-4"
					:autofocus="requiredPassword"
					@keyup-enter="login()"
				>
				<template v-slot:append>
					<v-icon @click.stop="showPassword = !showPassword">{{ showPassword ? 'mdi-eye' : 'mdi-eye-off' }}</v-icon>
				</template>
				</forms-text-field>
                
				<div class="justify-end d-flex" v-if="requiredPassword">
					<v-btn small text color="primary" class="mt-2" @click.stop="resetPassword()">Esqueceu sua senha?</v-btn>
				</div>

				<v-alert v-if="message" color="orange lighten-4" dark icon="mdi-alert-circle" border="left" prominent class="mt-4 orange--text text--darken-1" outlined>
                    {{ message }}
                </v-alert>

				<ButtonsDefault 
					v-if="requiredPassword"
					label="Entrar" 
					color="primary"
					block
                    class="mt-4"
					@click="login()"
					:loading="loading"
					:disabled="loading"
				/>

				<ButtonsDefault 
					v-if="!requiredPassword"
					label="Avançar" 
					color="primary"
					block
                    class="mt-4"
					@click="sendEmail()"
					:loading="loading"
					:disabled="loading"
				/>

              </v-card-text>

			  	<v-card-actions class="justify-end">
					<v-btn small text color="teal" class="mr-1 mb-1" @click.stop="toAccessTeacher()">Sou Professor <v-icon right>mdi-school-outline</v-icon></v-btn>
				</v-card-actions> 
            </v-card>

			<div class="d-flex justify-end mt-2 pr-2">
				<v-btn
					v-for="link in links"
					:key="link.icon"
					:href="link.url"
					:color="link.color"
					target="_blank"
					icon
				>
				<v-icon>{{ link.icon }}</v-icon>
				</v-btn>
			</div>

          </v-col>
        </v-row>
      </v-container>
    </v-main>
</template>

<script setup>

import { ref, getCurrentInstance, onMounted } from 'vue';
import { useStore } from '@/composables/useStore';

const store = useStore();
const { proxy } = getCurrentInstance();

const ActionRequest = (action, params) => store.dispatch(action, params);

const email = ref('');
const password = ref('');
const errors = ref({});
const message = ref('');
const showPassword = ref(false);
const loading = ref(false);
const requiredPassword = ref(false);

const rememberEmail = ref(false);

const links = [
  { icon: 'mdi-web', color: 'blue darken-2', url: 'https://www.emcsoft.com.br' },
  { icon: 'mdi-instagram', color: 'blue darken-2', url: 'https://www.instagram.com/emcsoft/' },
  { icon: 'mdi-linkedin', color: 'blue darken-2', url: 'https://www.linkedin.com/company/emcsoft-sysclass/about/' },
  { icon: 'mdi-youtube', color: 'blue darken-2', url: 'https://www.youtube.com/channel/UCNCXoc1PeaMTjC_tVCX61kA' },
]

const STORAGE_KEY = 'sysclass_remember_email';

const loadRememberedEmail = () => {
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		if (raw) {
			const parsed = JSON.parse(raw);
			if (parsed?.email) {
				email.value = parsed.email;
			}
			if (typeof parsed?.remember === 'boolean') {
				rememberEmail.value = parsed.remember;
			}
		}
	} catch (e) {
		// ignore localStorage errors but log for debugging
		// eslint-disable-next-line no-console
		console.warn('loadRememberedEmail error', e);
	}
}

const saveRememberedEmail = () => {
	try {
		if (rememberEmail.value && email.value) {
			localStorage.setItem(STORAGE_KEY, JSON.stringify({ email: email.value, remember: true }));
		} else {
			localStorage.removeItem(STORAGE_KEY);
		}
	} catch (e) {
		// ignore localStorage errors but log for debugging
		// eslint-disable-next-line no-console
		console.warn('saveRememberedEmail error', e);
	}
}

onMounted(() => {
	loadRememberedEmail();
});

const sendEmail = () => {
    
	loading.value = true;
	errors.value = {};
	message.value = '';

	// persist email if requested
	saveRememberedEmail();

	ActionRequest('domainAccessLogin/ActionPostCheckEmail', { email: email.value })
		.then(response => {

			if(response.data.is_auth_password) {
				requiredPassword.value = true;
				loading.value = false;
			} else {
				window.location.href = response.data.authorize_url;
			}
		})
		.catch(error => {
			errors.value = error.errors || {};
			message.value = error?.message;
			loading.value = false;
		})
}

const login = () => {
	loading.value = true;
	errors.value = {};
	message.value = '';

	// persist email if requested
	saveRememberedEmail();

	ActionRequest('auth/ActionDoLogin', { email: email.value, password: password.value })
		.then(() => {
			proxy.$router.push({ name: 'worktime' }).catch(() => {});
		})
		.catch(error => {
			message.value = error?.message;
		})
		.finally(() => {
			loading.value = false;
		});
}

const toAccessTeacher = () => {
	proxy.$router.push({ name: 'access-teacher' });
}

const resetPassword = () => {
	proxy.$router.push({ name: 'reset_password' });
}

</script>

<style scoped>
.v-card {
  border-radius: 12px;
}
.v-img {
  margin: 0 auto;
}
</style>